import React, { useEffect, useState, useCallback } from 'react';
import { ChevronDown, ChevronUp, Eye, Edit, Trash2, UserCheck, MoreHorizontal, Search, ArrowLeft, ChevronLeft, ChevronRight,  UserPlus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Textarea } from '../../components/ui/textarea';
import { cn } from '../../components/lib/utils'; 
import { motion, useAnimation } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getAllCandidates, getCandidateApplications, MarkCandidateStatus, updateCandidateStatus } from '../../services/candidateapi';
import { toast } from 'react-hot-toast';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { useRbac } from '../../context';

interface Candidate {
  id: string;
  name: string;
  email: string;
  phone: string;
  highestQualification: string;
  totalExperienceYears: number;
  totalExperienceMonths: number;
  skills: string[];
  knownTools?: string[];
  currentLocationDetails: {
    city: string;
    fullAddress: string;
  };
  profileSource: string;
  createdAt: string;
  profileCreatedAt: string;
  candidateProfileStrength: number;
  availabilityStatus: string;
  allowDuplicateWithinPeriod: boolean;
  sourcedBy: {
    id: string;
    username: string;
    email: string;
  };
}

interface GetCandidateApplicationApp {
  applicationId: string;
  job: {
    id: string;
    title: string;
    companyName: string;
  };
  appliedOn: string;
  currentStatus: {
    status: string;
    timestamp: string;
    updatedBy: {
      id: string;
      username: string;
      email: string;
    };
  };
  sourcedBy: {
    id: string;
    username: string;
    email: string;
  };
}

interface CandidatesTableProps {
  candidates: Candidate[];
  setCandidates: (candidates: Candidate[]) => void;
  onCandidateSelect: (candidate: Candidate) => void;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort: (column: string) => void;
  onStatusChange: (candidate: Candidate, newStatus: string, comment?: string) => void;
  candidatesLoading:boolean
  onToggleOverrideStatus: (candidate: Candidate, checked: boolean, comment?: string) => void;
  // statusChangeLoading: string | null;
}

const CandidatesTable: React.FC<CandidatesTableProps> = ({
  candidates,
  setCandidates,
  onCandidateSelect,
  sortBy,
  sortOrder,
  onSort,
  onStatusChange,
  onToggleOverrideStatus,
  candidatesLoading
}) => {
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCandidateApplications, setSelectedCandidateApplications] = useState<GetCandidateApplicationApp[]>([]);
  const [selectedCandidateName, setSelectedCandidateName] = useState('');
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [newStatus, setNewStatus] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [isCommentDialogOpen, setIsCommentDialogOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isToggleConfirmDialogOpen, setIsToggleConfirmDialogOpen] = useState(false);
  const [toggleComment, setToggleComment] = useState('');
  const [toggleCandidate, setToggleCandidate] = useState<Candidate | null>(null);
  const [pendingToggleStatus, setPendingToggleStatus] = useState<boolean | null>(null);
  const [actionCandidate, setActionCandidate] = useState<Candidate | null>(null);
  const [progress, setProgress] = useState(0);
  const controls = useAnimation();
  const DURATION = 1;
  const rbac = useRbac();
  const userRole = rbac?.user?.role;
  useEffect(() => {
    console.log('Candidates array:', candidates);
  }, [candidates]);

  useEffect(() => {
    if (isDialogOpen && actionCandidate?.id) {
      console.log(`Opening application history for candidate: ${actionCandidate.name}`);
      setProgress(0);
      const target = selectedCandidateApplications.length ?? 0;
      let start = 0;
      const step = () => {
        start += 1;
        setProgress(start);
        if (start < target) {
          setTimeout(step, (DURATION * 1000) / target);
        }
      };
      if (target > 0) {
        step();
      }
    } else {
      setProgress(0);
    }
  }, [isDialogOpen, actionCandidate, selectedCandidateApplications]);

  useEffect(() => {
    controls.start('visible');
  }, [controls]);

  const handleViewCandidate = async (candidate: Candidate) => {
    try {
      const response = await getCandidateApplications({ candidateId: candidate.id });
      const apps = (response.data?.applications as GetCandidateApplicationApp[]) ?? [];
      setSelectedCandidateName(candidate.name);
      setSelectedCandidateApplications(apps);
      setActionCandidate(candidate);
      setIsDialogOpen(true);
    } catch (err: any) {
      toast.error(err.message || 'Failed to fetch candidate applications');
    }
  };

  const handleStatusChange = (candidate: Candidate) => {
    setActionCandidate(candidate);
    setSelectedCandidateId(candidate.id);
    const targetStatus = candidate.availabilityStatus?.toLowerCase() === 'placed' ? 'Available' : 'Placed';
    setNewStatus(targetStatus);
    if (targetStatus === 'Placed') {
      setComment('');
      setIsCommentDialogOpen(true);
    } else {
      setIsConfirmDialogOpen(true);
    }
  };

  const handleToggleStatus = (candidate: Candidate, checked: boolean) => {
    // Show confirmation dialog before toggling
    setToggleCandidate(candidate);
    setPendingToggleStatus(checked);
    setToggleComment('');
    setIsToggleConfirmDialogOpen(true);
  };

  const confirmToggleStatus = async () => {
    if (!toggleCandidate || pendingToggleStatus === null) return;
    try {
      await onToggleOverrideStatus(toggleCandidate, pendingToggleStatus, toggleComment);
      setIsToggleConfirmDialogOpen(false);
      setToggleComment('');
      setToggleCandidate(null);
      setPendingToggleStatus(null);
    } catch (err: any) {
      // Error is already handled in onToggleOverrideStatus
    }
  };

  const confirmStatusChange = async () => {
    if (!selectedCandidateId || !newStatus || !actionCandidate) return;
    try {
      await onStatusChange(actionCandidate, newStatus, comment || 'Status updated from Candidates Table');
      toast.success(`Candidate status updated to ${newStatus}`);
      setIsCommentDialogOpen(false);
      setIsConfirmDialogOpen(false);
      setComment('');
      setSelectedCandidateId(null);
      setNewStatus(null);
      setActionCandidate(null);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update candidate status');
    }
  };

  const handleEditCandidate = (candidate: Candidate) => {
    navigate(`/edit-candidate/${candidate.id}`);
  };

  const handleDeleteCandidate = (candidate: Candidate) => {
    console.log(`Delete candidate with ID: ${candidate.id}`);
    toast.success('Candidate deleted successfully');
    setCandidates(candidates.filter(c => c.id !== candidate.id));
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'applied':
        return 'bg-white text-orange-600 border-orange-400 hover:border-orange-700 hover:bg-white hover:text-orange-700';
      case 'offered':
        return 'bg-gray-100 text-gray-700 border-gray-700 hover:border-gray-500 hover:bg-white hover:text-gray-700';
      case 'interview completed':
        return 'bg-green-100 text-green-600 border-green-600 hover:border-green-800 hover:bg-white hover:text-green-800';
      case 'selected':
        return 'bg-emerald-100 text-emerald-800 border-emerald-300';
      case 'rejected':
        return 'bg-rose-100 text-rose-800 border-rose-300';
      case 'placed':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'available':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const SortableHeader: React.FC<{ column: string; children: React.ReactNode; className?: string }> = ({
    column,
    children,
    className,
  }) => (
    <th
      className={cn(
        'px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors sticky top-0 bg-[#FFF5F2] z-10',
        className,
      )}
      onClick={() => onSort(column)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortBy === column && (
          sortOrder === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
        )}
      </div>
    </th>
  );

  const renderActionsDropdown = (candidate: Candidate) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleViewCandidate(candidate)}>
          <Eye className="mr-2 h-4 w-4" />
          View Applications
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleEditCandidate(candidate)}>
          <Edit className="mr-2 h-4 w-4" />
          Edit Candidate
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleDeleteCandidate(candidate)}>
          <Trash2 className="mr-2 h-4 w-4" />
          Delete Candidate
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleStatusChange(candidate)}>
          <UserCheck className="mr-2 h-4 w-4" />
          {candidate.availabilityStatus?.toLowerCase() === 'placed' ? 'Set as Open' : 'Set as Placed'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="bg-background relative min-w-full">
        <table className="min-w-full divide-y divide-border">
          <thead style={{ backgroundColor: '#FFF5F2' }}>
            <tr>
              <th className="px-4 py-3 w-12 sticky top-0 bg-[#FFF5F2] z-10"></th>
              <SortableHeader column="name" className="min-w-48 sticky left-0 bg-[#FFF5F2] z-20 border-r border-border">
                Name
              </SortableHeader>
              <SortableHeader column="email" className="bg-[#FFF5F2] min-w-64">Email</SortableHeader>
              <SortableHeader column="phone" className="bg-[#FFF5F2] min-w-32">Phone</SortableHeader>
              <SortableHeader column="availabilityStatus" className="bg-[#FFF5F2] min-w-32">Status</SortableHeader>
              {/* <SortableHeader column="currentLocationDetails.city" className="bg-[#FFF5F2] min-w-48">Location</SortableHeader> */}
              <SortableHeader column="sourcedBy.username" className="bg-[#FFF5F2] min-w-48">Sourced By</SortableHeader>
              <SortableHeader column="createdAt" className="bg-[#FFF5F2] min-w-40">Created At</SortableHeader>
              {/* <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-32 sticky top-0 bg-[#FFF5F2] z-10">
                Actions
              </th> */}
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-32 sticky top-0 bg-[#FFF5F2] z-10">
                Change Status
              </th>
              {userRole === 'ADMIN' && (
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-32 sticky top-0 bg-[#FFF5F2] z-10">
                  Allow Duplication
                </th>
              )}
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-32 sticky top-0 bg-[#FFF5F2] z-10">
                View Applications
              </th>
              
            </tr>
          </thead>

            {candidatesLoading ? <div className="relative top-[23vh] left-[50vh]"> 
            <p className="flex items-center gap-2 text-gray-600">
  <svg
    className="animate-spin h-5 w-5 text-orange-500"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    ></circle>
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
    ></path>
  </svg>
  Loading...
</p></div> :  candidates.length === 0 ? (
              <div className="text-center py-12">
          <p className="text-muted-foreground">No candidates found matching your criteria.</p>
        </div>
      ):candidates.map((candidate) => (
        <React.Fragment key={candidate.id}>
                <tbody className="bg-background divide-y divide-border ">
                <tr
                  className={cn('hover:bg-muted/30 transition-colors cursor-pointer')}
                  onClick={() => onCandidateSelect(candidate)}
                >
                  <td className="px-4 py-4"></td>
                  <td className="px-4 py-4 sticky left-0 bg-white z-10 border-r border-border relative group">
                    <div className="flex items-center gap-3">
                      <span className="font-medium transition-colors cursor-pointer text-foreground hover:text-primary">
                        {candidate.name}
                      </span>
                      
                    </div>
                    <div className="absolute top-0 left-[calc(100%+0.5rem)] mt-1 w-80 p-4 bg-white shadow-lg rounded border border-border opacity-0 pointer-events-none transition-opacity group-hover:opacity-100 group-hover:pointer-events-auto z-30">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-lg">{candidate.name}</h4>
                        <Badge variant="secondary" className="text-xs">
                          {candidate.availabilityStatus || 'N/A'}
                        </Badge>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Qualification:</span>
                          <span className="font-medium">{candidate.highestQualification}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Location:</span>
                          {/* <div className="max-w-48 truncate" title={candidate.currentLocationDetails.fullAddress}>
                            {candidate.currentLocationDetails.fullAddress}
                          </div> */}
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Phone:</span>
                          <span className="font-medium">{candidate.phone}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Skills:</span>
                          <p className="font-medium mt-1 break-words">
                            {candidate.skills?.length ? candidate.skills.slice(0, 3).join(", ") : "N/A"}
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Known Tools:</span>
                          <p className="font-medium mt-1 break-words">
                            {candidate.knownTools?.length ? candidate.knownTools.slice(0, 3).join(", ") : "N/A"}
                          </p>
                        </div>
                      </div>
                      <div className="pt-2 border-t border-border">
                        <div className="text-xs text-muted-foreground">
                          Joined {new Date(candidate.profileCreatedAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-foreground">
                    <span
                      className="cursor-pointer hover:text-orange-600"
                      title="Copy email"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(candidate.email);
                        toast.success('Email copied!');
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.textDecoration = 'underline';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.textDecoration = 'none';
                      }}
                    >
                      {candidate.email}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-foreground">
                    <span
                      className="cursor-pointer hover:text-orange-600"
                      title="Copy phone number"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(candidate.phone);
                        toast.success('Phone number copied!');
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.textDecoration = 'underline';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.textDecoration = 'none';
                      }}
                    >
                      {candidate.phone}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <Badge className={getStatusBadgeColor(candidate.availabilityStatus)}>
                      {candidate.availabilityStatus || 'N/A'}
                    </Badge>
                  </td>
                  <td className="px-4 py-4 text-sm text-foreground">{candidate.sourcedBy?.username || 'N/A'}</td>
                  <td className="px-4 py-4 text-sm text-muted-foreground">
                    {new Date(candidate.profileCreatedAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-4"> 
                   <Button
                        onClick={() => handleStatusChange(candidate)}
                        variant="outline"
                        size="sm"
                        className={`
                          flex items-center gap-2 rounded-lg px-3 py-1.5
                          border border-orange-300 text-orange-600 font-medium
                          bg-white shadow-sm
                          hover:bg-orange-50 hover:shadow-md hover:border-orange-400
                          active:scale-95
                          transition-all duration-200
                        `}
                      >
                        {candidate.availabilityStatus?.toLowerCase() === "placed" ? (
                          <UserPlus className="h-4 w-4" />
                        ) : (
                          <UserCheck className="h-4 w-4" />
                        )}
                        {candidate.availabilityStatus?.toLowerCase() === "placed"
                          ? "Set as Open"
                          : "Set as Placed"}
                      </Button>
                      </td>

                      {userRole === 'ADMIN' && (
                        <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}> 
                          <Switch
                            checked={!!candidate.allowDuplicateWithinPeriod}
                            onCheckedChange={(checked) => handleToggleStatus(candidate, checked)}
                            aria-label={`Toggle status for ${candidate.name}`}
                          />
                        </td>
                      )}
                  <td className="px-4 py-4"> 

                   <Button
                       onClick={() => handleViewCandidate(candidate)}
                        variant="outline"
                        size="sm"
                        className={`
                          flex items-center gap-2 rounded-lg px-3 py-1.5
                          border border-orange-300 text-orange-600 font-medium
                          bg-white shadow-sm
                          hover:bg-orange-50 hover:shadow-md hover:border-orange-400
                          active:scale-95
                          transition-all duration-200
                        `}
                      >
                        View</Button>
                      </td>
                </tr>
          </tbody>
              </React.Fragment>
            ))}
        </table>

      {/* Application History Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-white shadow-lg rounded-lg border border-gray-200 max-w-md px-[16px] py-[25px] gap-2">
          <DialogHeader>
            <DialogTitle className="font-semibold text-lg mb-4 text-gray-800">
              Application History of <span className="font-bold text-[#EC6E51]">{selectedCandidateName}</span>
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[400px]">
            {selectedCandidateApplications.length === 0 ? (
              <p className="text-sm text-gray-500">No applications found for {selectedCandidateName}.</p>
            ) : (
              selectedCandidateApplications
                .sort((a, b) => new Date(b.appliedOn).getTime() - new Date(a.appliedOn).getTime())
                .map((app, index) => (
                  <div
                    key={index}
                    className="relative flex items-start border-b border-gray-100 pb-4 last:border-b-0"
                  >
                    <div className="relative flex items-center justify-center w-6 h-6 mr-4">
                      <motion.div
                        className={`w-4 h-4 z-10 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors duration-300 ${
                          index < progress ? 'bg-[#ED795F] border-[#ED795F] text-white' : 'bg-white border-gray-300'
                        }`}
                        initial={{ scale: 1, opacity: 1 }}
                        animate={{
                          scale: index < progress ? [1, 1.2, 1] : 1,
                          opacity: index < progress ? [1, 0.8, 1] : 1,
                        }}
                        transition={{
                          duration: 0.8,
                          repeat: index < progress && app.currentStatus.status === 'Active' ? Infinity : 0,
                          ease: 'easeInOut',
                        }}
                      >
                        {index < progress && app.currentStatus.status !== 'Active' && (
                          <svg
                            className="w-3 h-3"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </motion.div>
                      {index < progress && app.currentStatus.status === 'Active' && (
                        <>
                          <motion.div
                            className="absolute left-0 rounded-full border-2 border-[#ED795F] bg-transparent z-0"
                            style={{
                              width: '75%',
                              height: '75%',
                              top: '13%',
                              left: '10.2%',
                              transform: 'translate(-50%, -50%)',
                            }}
                            animate={{
                              scale: [1, 1.8, 2.4],
                              opacity: [0.7, 0.4, 0],
                            }}
                            transition={{
                              duration: 1,
                              repeat: Infinity,
                              ease: 'easeOut',
                            }}
                          />
                          <motion.div
                            className="absolute rounded-full border-2 border-[#ED795F] bg-transparent z-0"
                            style={{
                              width: '75%',
                              height: '75%',
                              top: '13%',
                              left: '10.2%',
                              transform: 'translate(-50%, -50%)',
                            }}
                            animate={{
                              scale: [1, 1.8, 2.4],
                              opacity: [0.7, 0.4, 0],
                            }}
                            transition={{
                              duration: 1,
                              repeat: Infinity,
                              ease: 'easeOut',
                              delay: 0.3,
                            }}
                          />
                        </>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`font-semibold text-sm ${index < progress ? 'text-gray-800' : 'text-gray-400'}`}>
                          {app.job.title}
                        </span>
                      </div>
                      <div className="text-xs space-y-1">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Company:</span>
                          <span className={`${index < progress ? 'text-gray-600' : 'text-gray-400'} font-medium`}>
                            {app.job.companyName}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Applied On:</span>
                          <span className={`${index < progress ? 'text-gray-600' : 'text-gray-400'} font-medium`}>
                            {new Date(app.appliedOn).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Status:</span>
                          <span className={`${index < progress ? 'text-gray-600' : 'text-gray-400'} font-medium`}>
                            {app.currentStatus.status || 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Sourced By:</span>
                          <span className={`${index < progress ? 'text-gray-600' : 'text-gray-400'} font-medium`}>
                            {app.sourcedBy?.username || 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Updated By:</span>
                          <span className={`${index < progress ? 'text-gray-600' : 'text-gray-400'} font-medium`}>
                            {app.currentStatus.updatedBy?.username || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Comment Dialog for Setting as Placed */}
      <Dialog open={isCommentDialogOpen} onOpenChange={setIsCommentDialogOpen}>
        <DialogContent className="sm:max-w-[500px] rounded-2xl bg-white/95 backdrop-blur-sm shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">Set Candidate as Placed</DialogTitle>
            <DialogDescription className="text-gray-600">
              Please provide a comment for setting the candidate status to Placed.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Enter your comment here..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full h-24 rounded-xl border-gray-200 focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsCommentDialogOpen(false);
                setComment('');
                setSelectedCandidateId(null);
                setNewStatus(null);
                setActionCandidate(null);
              }}
              className="rounded-xl border-gray-200"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmStatusChange}
              disabled={!comment.trim()}
              className="rounded-xl bg-orange-600 text-white hover:bg-orange-700"
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog for Setting as Available */}
      <AlertDialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Status Change</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to set the candidate <strong>{actionCandidate?.name}</strong>'s status to Available?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setIsConfirmDialogOpen(false);
              setSelectedCandidateId(null);
              setNewStatus(null);
              setActionCandidate(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmStatusChange}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmation Dialog for Toggle Switch */}
      <Dialog 
        open={isToggleConfirmDialogOpen} 
        onOpenChange={(open) => {
          setIsToggleConfirmDialogOpen(open);
          if (!open) {
            // Reset state when dialog is closed
            setToggleComment('');
            setToggleCandidate(null);
            setPendingToggleStatus(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-[500px] rounded-2xl bg-white/95 backdrop-blur-sm shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              Confirm Status Change
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              Are you sure you want to {pendingToggleStatus ? 'enable' : 'disable'} duplicate application override for{' '}
              <strong>{toggleCandidate?.name}</strong>?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Comment (Optional)
            </label>
            <Textarea
              placeholder="Enter your comment here..."
              value={toggleComment}
              onChange={(e) => setToggleComment(e.target.value)}
              className="w-full h-24 rounded-xl border-gray-200 focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsToggleConfirmDialogOpen(false);
                setToggleComment('');
                setToggleCandidate(null);
                setPendingToggleStatus(null);
              }}
              className="rounded-xl border-gray-200"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmToggleStatus}
              className="rounded-xl bg-orange-600 text-white hover:bg-orange-700"
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default function ViewCandidates({ onAddCandidate }: { onAddCandidate?: () => void }) {
  const [searchTerm, setSearchTerm] = useState("")
  const [isLoading, setIsLoading] = useState(true);

  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusChangeLoading, setStatusChangeLoading] = useState<string | null>(null);
  const navigate=useNavigate();

  const fetchCandidates = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getAllCandidates({
        page: currentPage,
        limit: 10,
        search: searchTerm,
        // sortBy,
        // sortOrder,
      });
     const apps = response.data.candidates as unknown as Candidate[];
      setCandidates(apps);
      setTotalPages(response.totalPages);

    } catch (err: any) {
      toast.error(err.message || 'Failed to fetch candidates');
    }
    finally {
      setIsLoading(false);
    }
  }, [currentPage, sortBy, sortOrder,searchTerm]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleStatusChange = async (candidate: Candidate, newStatus: string, comment?: string) => {
    setStatusChangeLoading(candidate.id);
    try {
      await MarkCandidateStatus(candidate.id, newStatus, comment || 'Status updated from Candidates Table');
      setCandidates(candidates.map(c =>
        c.id === candidate.id ? { ...c, availabilityStatus: newStatus } : c
      ));
    } catch (err: any) {
      toast.error(err.message || 'Failed to update candidate status');
    } finally {
      setStatusChangeLoading(null);
    }
  };

  const handleToggleOverrideStatus = async (candidate: Candidate, checked: boolean, comment?: string) => {
    const newStatus = !!checked;
    try {
      await updateCandidateStatus(candidate.id, newStatus, comment);
      setCandidates(candidates.map(c =>
        c.id === candidate.id ? { ...c, allowDuplicateWithinPeriod: newStatus } : c
      ));
      toast.success(`Candidate override status ${newStatus ? 'enabled' : 'disabled'} successfully`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update candidate status');
    }
  };

   const handleBackNavigation = () => {
    navigate(-1);
  };
  const renderPagination = () => {
    const pageNumbers = [];
    const maxPagesToShow = 6;

    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          disabled={currentPage === 1 || isLoading}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>

        {pageNumbers.map(page => (
          <Button
            key={page}
            variant={currentPage === page ? 'default' : 'outline'}
            size="sm"
            onClick={() => setCurrentPage(page)}
            disabled={isLoading}
            className={currentPage === page ? 'bg-[#EC6E51] text-primary-foreground' : ''}
          >
            {page}
          </Button>
        ))}

        {endPage < totalPages && (
          <>
            <span className="text-sm text-muted-foreground">...</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={isLoading}
            >
              {totalPages}
            </Button>
          </>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages || isLoading}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    );
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
    <div className="flex-shrink-0 bg-white border-b border-gray-200 px-3 sm:px-6 py-4">
          <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Button variant="ghost" size="sm" className="p-1 sm:p-2" onClick={handleBackNavigation}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">Candidates</h1>
                <p className="text-xs sm:text-sm text-gray-500">
                    {/* Total Openings: {totalOpenings} |  */}

                </p>
              </div>
            </div>
            <div className="relative flex-1 sm:flex-none">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search Candidates..."
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                className="pl-10 w-full sm:w-64 border-gray-300"
              />
            </div>

            {/* <Button
              variant="outline"
              size="sm"
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="sm:hidden"
            >
              <Filter className="h-4 w-4" />
            </Button> */}
          </div>

         
        </div>
    <div className="flex-1 min-h-0 overflow-auto">
    <CandidatesTable
      candidates={candidates}
      setCandidates={setCandidates}
      onCandidateSelect={(candidate) => console.log('Selected candidate:', candidate)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSort={handleSort}
      onStatusChange={handleStatusChange}
      onToggleOverrideStatus={handleToggleOverrideStatus}
      candidatesLoading={isLoading}
      // statusChangeLoading={statusChangeLoading}
    />
    </div>
    <div className="flex flex-shrink-0 items-center gap-2 justify-end p-4 border-t border-gray-200 bg-white">
        {renderPagination()}
      </div>
    </div>
  );
}